import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2Button,
  V2Table,
  ConfirmDialog,
  LoadingState,
  StatePanel,
  type V2TableColumn,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { amountColumnHeader, formatRialFa } from '../../lib/finance-display.js';
import type {
  PurchaseRequestDetail,
  PurchaseRequestLine,
  PurchaseRequestStatus,
} from '../../lib/types/procurement.js';
import { requestStatusOf } from '../../lib/types/procurement.js';
import { ProcurementAttachmentsCard } from './ProcurementAttachmentsCard.js';

const STATUS_TONE: Record<PurchaseRequestStatus, V2BadgeTone> = {
  draft: 'neutral',
  submitted: 'info',
  approved: 'brand',
  rejected: 'danger',
  cancelled: 'neutral',
};

type Confirming = 'submit' | 'approve' | 'reject' | 'cancel' | null;

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Procurement.dc.html §01 PURCHASE REQUESTS — detail/review panel
 * archetype: ENTITY_DETAIL + approval workflow
 * status: KIT_CONFORMANT — actions shown are exactly the real transitions
 *   (`PURCHASE_REQUEST_TRANSITIONS` in packages/modules/procurement/src/
 *   transitions.ts): draft → submit/cancel; submitted → approve/reject/
 *   cancel; every other status is terminal. The board's "بازگشت برای
 *   اصلاح" (return for correction) button has no matching transition —
 *   only approve/reject exist from submitted — so it is not built. The
 *   review stepper (4-step progress bar) is not shown either: it implies
 *   a distinct "تأیید" step separate from the terminal `approved` status,
 *   which the real 5-status set doesn't have. Rejection note is optional
 *   in the real contract (`rejectPurchaseRequest`'s `note` is `string |
 *   undefined`) but required here client-side, matching the board's own
 *   evident intent without claiming a backend rule that doesn't exist.
 * state reference: ready · permission_denied · not_found · network_error · validation
 */
export function RequestDetailPage() {
  const { unit } = useAmountDisplay();
  const { slug, ref } = useParams<{ slug: string; ref: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const request = useApiGet<PurchaseRequestDetail>(
    slug && ref ? `${base}/api/v1/procurement/requests/${ref}` : null,
    [slug, ref, refreshKey],
  );

  if (!slug || !ref) return null;

  const requestStatus =
    request.status === 'ready' ? requestStatusOf(request.data.statusLabel) : undefined;

  async function runAction(action: 'submit' | 'approve' | 'reject' | 'cancel') {
    setSubmitting(true);
    setError(null);
    try {
      const body = action === 'reject' ? { note: rejectNote.trim() } : undefined;
      await apiPost(`${base}/api/v1/procurement/requests/${ref}/${action}`, body);
      setRefreshKey((k) => k + 1);
      setRejectNote('');
    } catch (err) {
      setError(err instanceof ApiError ? err.envelope.error.message : 'ثبت اقدام ناموفق بود.');
    } finally {
      setSubmitting(false);
      setConfirming(null);
    }
  }

  function requestReject() {
    if (!rejectNote.trim()) {
      setError('برای رد درخواست، ثبت دلیل الزامی است.');
      return;
    }
    setError(null);
    void runAction('reject');
  }

  const lineColumns: readonly V2TableColumn<PurchaseRequestLine & { id: string }>[] = [
    {
      key: 'product',
      header: 'کالا / خدمت',
      render: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 500 }} className="ltr">
            {row.productRef}
          </span>
          {row.description ? (
            <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>{row.description}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'مقدار',
      align: 'end',
      render: (row) => <span className="sk-num">{row.quantity}</span>,
    },
    { key: 'uom', header: 'واحد', render: (row) => row.uomRef },
    {
      key: 'price',
      header: amountColumnHeader('قیمت برآوردی', unit),
      align: 'end',
      render: (row) => (
        <span className="sk-num">
          {row.estimatedUnitPriceRial ? formatRialFa(row.estimatedUnitPriceRial, unit) : '—'}
        </span>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="sk-breadcrumb">
        <span>تدارکات و خرید</span>
        <span>/</span>
        <Link to={`${base}/procurement/requests`}>درخواست‌های خرید</Link>
        <span>/</span>
        <b className="ltr" style={{ fontFamily: 'var(--sk-font-mono)' }}>
          {ref}
        </b>
      </div>

      {request.status === 'loading' ? <LoadingState /> : null}
      {request.status === 'error' ? (
        <StatePanel
          variant={
            request.error.status === 403
              ? 'permission_denied'
              : request.error.status === 404
                ? 'not_found'
                : 'network_error'
          }
          title={
            request.error.status === 403
              ? 'دسترسی ندارید'
              : request.error.status === 404
                ? 'پیدا نشد'
                : 'قابل نمایش نیست'
          }
        />
      ) : null}

      {request.status === 'ready' ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ margin: 0, fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>
              {request.data.title}
            </h1>
            <V2Badge tone={STATUS_TONE[requestStatus ?? 'draft'] ?? 'neutral'}>
              {request.data.statusLabel}
            </V2Badge>
          </div>

          <V2Card>
            <div
              style={{
                padding: '16px 20px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 16,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>شماره</span>
                <span
                  className="ltr sk-num"
                  style={{ fontSize: 14, fontWeight: 500, fontFamily: 'var(--sk-font-mono)' }}
                >
                  {request.data.ref}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>تاریخ درخواست</span>
                <span className="sk-num" style={{ fontSize: 14, fontWeight: 500 }}>
                  {request.data.requestedDate}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>موعد نیاز</span>
                <span className="sk-num" style={{ fontSize: 14, fontWeight: 500 }}>
                  {request.data.neededByDate ?? '—'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>انبار مقصد</span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>
                  {request.data.destinationWarehouseName ??
                    request.data.destinationWarehouseRef ??
                    '—'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>پروژه</span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>
                  {request.data.projectName ?? request.data.projectRef ?? '—'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>مرکز هزینه</span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>
                  {request.data.costCenterName ?? request.data.costCenterRef ?? '—'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>هدف</span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{request.data.purpose || '—'}</span>
              </div>
            </div>
          </V2Card>

          <V2Card>
            <V2CardHeader
              title="اقلام درخواست"
              actions={
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                  {request.data.lines.length} قلم
                </span>
              }
            />
            <V2Table
              columns={lineColumns}
              rows={request.data.lines.map((l) => ({ ...l, id: String(l.rowNumber) }))}
              zebra
            />
          </V2Card>

          {request.data.approvals.length > 0 ? (
            <V2Card>
              <V2CardHeader title="سابقهٔ تأیید" />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {request.data.approvals.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      padding: '12px 20px',
                      borderTop: i > 0 ? '1px solid var(--sk-divider)' : undefined,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <V2Badge tone={a.decision === 'approved' ? 'success' : 'danger'}>
                        {a.decision === 'approved' ? 'تأیید' : 'رد'}
                      </V2Badge>
                      <span
                        className="sk-num"
                        style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}
                      >
                        {a.decidedAt.slice(0, 10)}
                      </span>
                    </div>
                    {a.note ? (
                      <span style={{ fontSize: 13, color: 'var(--sk-fg)' }}>{a.note}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            </V2Card>
          ) : null}

          <ProcurementAttachmentsCard
            base={base}
            targetType="request"
            targetRef={ref}
            readOnly={requestStatus === 'cancelled' || requestStatus === 'rejected'}
          />

          {error ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '13px 16px',
                borderRadius: 'var(--sk-r-md)',
                background: 'var(--sk-danger-soft)',
                border: '1px solid color-mix(in oklch, var(--sk-danger), transparent 78%)',
              }}
            >
              <span style={{ fontSize: 13, color: 'var(--sk-fg)' }}>{error}</span>
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 8 }}>
            {requestStatus === 'draft' ? (
              <>
                <V2Button
                  variant="primary"
                  disabled={submitting}
                  onClick={() => setConfirming('submit')}
                >
                  ارسال درخواست
                </V2Button>
                <V2Button
                  variant="ghost"
                  style={{ color: 'var(--sk-danger)' }}
                  disabled={submitting}
                  onClick={() => setConfirming('cancel')}
                >
                  لغو درخواست
                </V2Button>
              </>
            ) : null}
            {requestStatus === 'submitted' ? (
              <>
                <V2Button
                  variant="primary"
                  disabled={submitting}
                  onClick={() => setConfirming('approve')}
                >
                  تأیید درخواست
                </V2Button>
                <V2Button
                  variant="ghost"
                  style={{ color: 'var(--sk-danger)' }}
                  disabled={submitting}
                  onClick={() => setConfirming('reject')}
                >
                  رد درخواست
                </V2Button>
                <V2Button
                  variant="ghost"
                  disabled={submitting}
                  onClick={() => setConfirming('cancel')}
                >
                  لغو درخواست
                </V2Button>
              </>
            ) : null}
            {requestStatus === 'approved' ? (
              <Link to={`${base}/procurement/orders?requestRef=${request.data.ref}`}>
                <V2Button variant="primary" icon="plus">
                  ساخت سفارش خرید
                </V2Button>
              </Link>
            ) : null}
          </div>
        </>
      ) : null}

      {confirming === 'submit' ? (
        <ConfirmDialog
          open
          title="ارسال درخواست خرید؟"
          message="پس از ارسال، درخواست برای بررسی در دسترس قرار می‌گیرد."
          confirmLabel={submitting ? 'در حال ثبت…' : 'ارسال'}
          cancelLabel="انصراف"
          onConfirm={() => void runAction('submit')}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
      {confirming === 'approve' ? (
        <ConfirmDialog
          open
          title="تأیید درخواست خرید؟"
          message="پس از تأیید، امکان ساخت سفارش خرید از این درخواست فراهم می‌شود."
          confirmLabel={submitting ? 'در حال ثبت…' : 'تأیید'}
          cancelLabel="انصراف"
          onConfirm={() => void runAction('approve')}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
      {confirming === 'cancel' ? (
        <ConfirmDialog
          open
          title="لغو درخواست خرید؟"
          message="این اقدام برگشت‌پذیر نیست."
          danger
          confirmLabel={submitting ? 'در حال ثبت…' : 'لغو درخواست'}
          cancelLabel="انصراف"
          onConfirm={() => void runAction('cancel')}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
      {confirming === 'reject' ? (
        <ConfirmDialog
          open
          title="رد درخواست خرید؟"
          message={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span>دلیل رد الزامی است و در سابقهٔ درخواست ثبت می‌شود.</span>
              <textarea
                className="sk-textarea"
                placeholder="دلیل را بنویسید"
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
              />
              {error ? (
                <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>{error}</span>
              ) : null}
            </div>
          }
          danger
          confirmLabel={submitting ? 'در حال ثبت…' : 'رد درخواست'}
          cancelLabel="انصراف"
          onConfirm={requestReject}
          onCancel={() => {
            setConfirming(null);
            setError(null);
          }}
        />
      ) : null}
    </div>
  );
}
